import {
  useState, useRef, useEffect, useCallback, 
} from 'react'
import useResizeObserver from 'use-resize-observer'
import { useSwipeable } from 'react-swipeable'
import styled from 'styled-components'
import { rgba } from 'polished'
import textLink, { textLinkWithHover } from '../styles/textLink'
import Background from './layout/Background'
import Container, { getMaxElementWidthInRem } from './layout/Container'
import Link from './Link'
import Image from './Image'
import Chevron from './icons/Chevron'

function InvestigationsCarsouel( { sectionTitle, investigations } ) {
  const [currentSlide, setCurrentSlide] = useState( 0 )
  const [slidesInView, setSlidesInView] = useState( 1 )
  const [isAtStart, setIsAtStart] = useState( true )
  const [isAtEnd, setIsAtEnd] = useState( false )
  const sliderRef = useRef( null )
  const { ref: carouselRef, width: carouselWidth } = useResizeObserver()

  const countSlidesInView = useCallback( () => Math.round( investigations.length / ( 
    sliderRef.current.offsetWidth / carouselWidth
  ) ), [carouselWidth, investigations.length] )
  
  const goToSlide = slideIndex => {
    if ( slideIndex >= 0 && slideIndex <= investigations.length - slidesInView ) {
      setCurrentSlide( slideIndex )
      setIsAtStart( slideIndex === 0 )
      setIsAtEnd( slideIndex === investigations.length - slidesInView )
    }
  }
  
  const previousSlide = () => goToSlide( currentSlide - 1 )
  const nextSlide = () => goToSlide( currentSlide + 1 )

  const bringIntoView = slideIndex => {
    if ( 
      slidesInView === 1 
      || slideIndex < currentSlide
    ) {
      goToSlide( slideIndex )
    }
    else {
      goToSlide( slideIndex - slidesInView + 1 )
    }
  }

  const swipeHandlers = useSwipeable( {
    onSwipedLeft: nextSlide,
    onSwipedRight: previousSlide,
  } )

  useEffect( () => {
    setSlidesInView( countSlidesInView() )
  }, [carouselWidth, countSlidesInView] )

  return (
    <Background color="blueGrey__light">
      <Container>
        <StyledInvestigationsCarousel>
          <StyledCarouselHeader>
            <h2>
              { sectionTitle }
              <Link href="/investigations">View all</Link>
            </h2>
            <StyledButtons $hide={ slidesInView >= investigations.length }>
              <button
                type="button"
                onClick={ previousSlide }
                disabled={ isAtStart }
              >
                Previous
                <Chevron />
              </button>
              <button
                type="button"
                onClick={ nextSlide }
                disabled={ isAtEnd }
              >
                Next
                <Chevron />
              </button>
            </StyledButtons>
          </StyledCarouselHeader>
          <div
            // eslint-disable-next-line react/jsx-props-no-spreading
            { ...swipeHandlers }
          >
            <StyledCarousel 
              ref={ carouselRef }
              $slideCount={ investigations.length }
              $currentSlide={ currentSlide }
            >
              <div ref={ sliderRef }>
                { investigations.map( ( inv, i ) => (
                  <InvestigationSlide
                    key={ inv.id }
                    index={ i }
                    investigation={ inv }
                    isInView={ 
                      currentSlide === i 
                      || ( 
                        slidesInView === 2 
                        && currentSlide + 1 === i 
                      ) 
                    }
                    bringIntoView={ bringIntoView }
                  />
                ) ) }
              </div>
            </StyledCarousel>
          </div>
        </StyledInvestigationsCarousel>
      </Container>
    </Background>
  )
}

function InvestigationSlide( {
  index, investigation, isInView, bringIntoView, 
} ) {
  const content = (
    <>
      <StyledSlideImage>
        <Image
          src={ investigation.mainImage[ 0 ].url }
          alt={ investigation.mainImage[ 0 ].imageAltText }
          layout="fill"
          objectFit="cover"
          sizes={ `
            ( max-width: 1019px ) 87vw,
            min( ${ getMaxElementWidthInRem( { cols: 2, gutter: 2 } ) }rem, calc( 43.5vw - 2rem ) )
          ` }
        />
      </StyledSlideImage>
      <StyledSlideContent>
        <h3>{ investigation.title }</h3>
        <div 
            // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={ { __html: investigation.introCopy } }
        />
        <span>Read more</span>
      </StyledSlideContent>
    </>
  )
  
  return (
    <StyledSlide>
      { isInView && (
        <Link href={ `/investigations/${ investigation.slug }` }>
          { content }
        </Link>
      ) }
      { !isInView && (
        <button
          type="button"
          onClick={ () => bringIntoView( index ) }
        >
          { content }
        </button>
      ) }
    </StyledSlide>
  )
}

const StyledInvestigationsCarousel = styled.div`
  padding: 3rem 0;
`

const StyledCarouselHeader = styled.div`
  display: flex;
  align-items: center;
  margin: 0 0 3rem;

  h2 {
    display: flex;
    flex-wrap: wrap;
    gap: 0 1rem;
    margin: 0;
    ${ p => p.theme.typo.generalSansSemiBold }
    font-size: ${ p => p.theme.typo.sizes.normal };

    a {
      ${ p => p.theme.typo.generalSans }
      color: ${ p => p.theme.colors.blueGrey__deep };
      ${ textLinkWithHover }
      text-decoration-color: currentColor;
    }
  }
`

const StyledButtons = styled.div<{ $hide: boolean }>`
  display: ${ p => p.$hide ? 'none' : 'flex' };
  margin: 0 -0.3rem 0 auto;
  padding-left: 1.5rem;

  button {
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 0.3rem;
    width: 48px;
    height: 48px;
    border-radius: 100%;
    text-indent: -999rem;
    background: none;
    border: 1px solid ${ p => p.theme.colors.black };
    cursor: pointer;
    transition: 
      opacity .3s linear,
      border-color .3s ease-out;

    svg {
      width: 12px;

      > path {
        stroke: ${ p => p.theme.colors.black };
        transition: stroke .3s ease-out;
      }
    }

    &:first-child {
      svg {
        transform: rotate( 180deg );
      }
    }

    &:disabled {
      opacity: 0.3;
    }

    &:not( :disabled ):hover {
      border-color: ${ p => p.theme.colors.red };

      svg > path {
        stroke: ${ p => p.theme.colors.red };
      }
    }
  }
`

interface StyledCarousel {
  $slideCount: number,
  $currentSlide: number,
}

const StyledCarousel = styled.div<StyledCarousel>`
  position: relative;
  overflow: visible;
  margin: 0 -1rem;

  > div {
    position: relative;
    display: flex;
    width: calc( 100% * ${ p => p.$slideCount } );
    left: ${ p => p.$currentSlide * -100 }%;
    transition: left .3s ease-out;
    
    @media ( min-width: 1020px ) {
      width: calc( 50% * ${ p => p.$slideCount } );
      left: ${ p => p.$currentSlide * -50 }%;
    }
  }
`

const StyledSlideImage = styled.div`
  position: relative;
  width: 100%;
  padding-bottom: 72%;

  img {
    filter: grayscale( 1 );
  }

  @media ( max-width: 1019px ) {
    padding-bottom: min( max( calc( 1000px - 100% ), 45% ), 110% );
  }
`

const StyledSlideContent = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  text-align: left;
  padding: 1.2rem 1.2rem 1.5rem;
  background: ${ p => rgba( p.theme.colors.red, 0.85 ) };
  color: ${ p => p.theme.colors.white };
  transition: background-color .3s ease-out;

  > * {
    max-width: 25rem;
  }

  h3 {
    margin: auto 0 1rem;
    ${ p => p.theme.typo.generalSansSemiBold }
    font-size: ${ p => p.theme.typo.sizes.medium };
  }

  > span {
    margin-top: 1.5rem;
    ${ p => p.theme.typo.generalSans }
    font-size: ${ p => p.theme.typo.sizes.small };
    ${ textLink }
    text-decoration-color: ${ p => p.theme.colors.white };
  }
`

const StyledSlide = styled.div`
  flex: 1 0;  

  > a,
  button {
    position: relative;
    display: block;
    width: calc( 100% - 2rem );
    margin: 0 1rem;
    padding: 0;
    background: none;
    border: 0;
    cursor: pointer;
    outline: inherit;
  }

  > a:hover ${ StyledSlideContent } {
    background: ${ p => rgba( p.theme.colors.black, 0.85 ) };
  }
`

export default InvestigationsCarsouel
